import { PayloadAction } from '@reduxjs/toolkit';
import { AAASMessages } from '@va/aaas/shared/constants';
import { getBillingMode, getIntpUpgradeMessage, selectAllowUpgrade } from '@va/aaas/shared/selectors';
import { AaasBillingMode, IntpUpgradeMessage } from '@va/aaas/shared/types';
import { activeWebsiteSuccessullyInitialized, RequiredPlan, switchActiveWebsite } from '@va/dashboard/actions/app';
import { CLOSE_MODAL, openModal } from '@va/dashboard/actions/ui';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { appHistory } from '@va/shared/router';
import { CONFIRM_UPGRADE_WITH_CANCELLED_SUBSCRIPTION } from '@va/standalone/shared/actions';
import { modalName } from '@va/standalone/shared/constants';
import { getIsSubscriptionPermanentlyCanceled, isWixClaimed } from '@va/standalone/shared/selectors';
import { getViewOnWixUrl, is3asApp, isWixApp, isWixMiniApp } from '@va/util/helpers';
import { openPricingPage } from '@va/wix-mini/shared/helpers';
import { getMetaSiteId } from '@va/wix/shared/selectors';
import { put, select, take } from 'redux-saga/effects';

export function* startUpgradeProcessSaga(
  action: PayloadAction<({ websiteId?: string } & RequiredPlan) | undefined>,
): Generator<any> {
  if (is3asApp()) {
    const billingMode: AaasBillingMode = yield select(getBillingMode);
    if (billingMode === AaasBillingMode.PlatformManaged) {
      const { aaasMinCredits } = action.payload ?? {};
      appHistory.push('/upgrade', aaasMinCredits === undefined ? undefined : { aaasMinCredits });
      return;
    }

    const allowUpgrade = yield select(selectAllowUpgrade);
    if (!allowUpgrade) return;

    const messageData = (yield select(getIntpUpgradeMessage)) as IntpUpgradeMessage;

    window.parent.postMessage(
      {
        type: AAASMessages.UPGRADE_BUTTON_CLICKED,
        data: messageData,
      },
      '*',
    );
    return;
  }

  if (isWixApp()) {
    const { wixPermissionPack } = action.payload ?? {};
    appHistory.push('/upgrade', wixPermissionPack === undefined ? undefined : { wixPermissionPack });
    return;
  }

  if (isWixMiniApp()) {
    openPricingPage();
    return;
  }

  const { websiteId } = action.payload ?? {};
  const currentInstanceId = yield select(getInstanceId);

  if (websiteId && currentInstanceId !== websiteId) {
    yield put(switchActiveWebsite({ websiteId: websiteId }));
    yield take(activeWebsiteSuccessullyInitialized.type);
  }

  const updatedInstanceId = yield select(getInstanceId);
  const wixClaimed = yield select(isWixClaimed);

  // On standalone for wix website, redirect to our app in wix
  if (wixClaimed) {
    const metaSiteId = (yield select(getMetaSiteId)) as string;
    const wixUrl = getViewOnWixUrl(metaSiteId);
    window.open(wixUrl, '_blank');
    return;
  }

  const isSubscriptionPermanentlyCanceled = yield select(getIsSubscriptionPermanentlyCanceled);
  if (isSubscriptionPermanentlyCanceled) {
    yield put(openModal(modalName.UPGRADE_WITH_CANCELLED_SUBSCRIPTION));
    const result = yield take(
      (action: { type: string; modalName?: string }) =>
        action.type === CONFIRM_UPGRADE_WITH_CANCELLED_SUBSCRIPTION ||
        (action.type === CLOSE_MODAL && action.modalName === modalName.UPGRADE_WITH_CANCELLED_SUBSCRIPTION),
    );
    if (result.type === CLOSE_MODAL) return;
  }

  appHistory.push(`/website/${updatedInstanceId}/upgrade`);
}
